import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import html2pdf from "html2pdf.js";
import {
  FileText, Download, Printer, RefreshCw, Check, X,
  ChevronLeft, User, Hash, Calendar, Award, AlertCircle, CheckCircle2
} from "lucide-react";

// ---- DATA STRUCTURES ----
const BASE_DOCS = [
  "RE-NEET Admit Card / Hall Ticket of NEET UG 2026 (June 2026)",
  "Fitness Certificate",
  "Age, Nationality & Domicile Certificate",
  "HSC Marksheet (Class 12th)",
  "SSC Passing Certificate (Date of Birth Proof)"
];

const CATEGORY_DOCS = {
  OPEN: [...BASE_DOCS],
  SC: [...BASE_DOCS, "Caste Certificate (SC)", "Caste Validity Certificate"],
  ST: [...BASE_DOCS, "Caste Certificate (ST)", "Caste Validity Certificate"],
  VJA: [...BASE_DOCS, "Caste Certificate (VJA)", "Caste Validity Certificate", "Non-Creamy Layer (NCL) Certificate (Minimum Valid up to 31/03/2027)"],
  NT1: [...BASE_DOCS, "Caste Certificate (NT1 – B)", "Caste Validity Certificate", "Non-Creamy Layer (NCL) Certificate (Minimum Valid up to 31/03/2027)"],
  NT2: [...BASE_DOCS, "Caste Certificate (NT2 – C)", "Caste Validity Certificate", "Non-Creamy Layer (NCL) Certificate (Minimum Valid up to 31/03/2027)"],
  NT3: [...BASE_DOCS, "Caste Certificate (NT3 – D)", "Caste Validity Certificate", "Non-Creamy Layer (NCL) Certificate (Minimum Valid up to 31/03/2027)"],
  OBC: [...BASE_DOCS, "Caste Certificate (OBC)", "Caste Validity Certificate", "Non-Creamy Layer (NCL) Certificate (Minimum Valid up to 31/03/2027)"],
  SBC: [...BASE_DOCS, "Caste Certificate (SBC)", "Caste Validity Certificate", "Non-Creamy Layer (NCL) Certificate (Minimum Valid up to 31/03/2027)"],
  EWS: [...BASE_DOCS, "EWS (Economically Weaker Section) Certificate (Issued on or after 01/04/2026)"],
  SEBC: [...BASE_DOCS, "Caste Certificate (SEBC)", "Caste Validity Certificate", "Non-Creamy Layer (NCL) Certificate (Minimum Valid up to 31/03/2027)"]
};

const RESERVATION_DOCS = {
  DEFENCE1: { label: "DEFENCE-1", docs: ["Ex-Servicemen Certificate", "Domicile Certificate of Defence Person"] },
  DEFENCE2: { label: "DEFENCE-2", docs: ["Actual Service Certificate", "Domicile Certificate of Defence Person"] },
  DEFENCE3: { label: "DEFENCE-3", docs: ["Actual Service Certificate", "Transfer Certificate", "Domicile Certificate of Defence Person"] },
  MKB: { label: "MKB", docs: ["Dispute Area Certificate (Annexure-E)", "Mother Tongue Certificate (Annexure-E)"] },
  HILLY: { label: "HILLY AREA", docs: ["Hilly Area Certificate", "Parent's Domicile Certificate (Hilly Area)", "Proof of 10th / 12th Passing from Hilly Area"] },
  PWD: { label: "PWD", docs: ["PWD Certificate (Annexure-D)"] },
  ORPHAN: { label: "ORPHAN", docs: ["Certificate from Women & Child Welfare Department"] },
  MINORITY: { label: "MINORITY", docs: ["Minority Affidavit"] }
};

const CAT_LABELS = {
  OPEN: 'OPEN', SC: 'SC', ST: 'ST', VJA: 'VJA',
  NT1: 'NT1 (B)', NT2: 'NT2 (C)', NT3: 'NT3 (D)',
  OBC: 'OBC', SBC: 'SBC', EWS: 'EWS', SEBC: 'SEBC'
};

// Premium Light Mode Card Style
const cardStyle = {
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: 20,
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05)'
};

export default function MedicalDocumentsChecklist() {
  const navigate = useNavigate();

  // Form input state
  const [formData, setFormData] = useState({
    candName: "",
    regNo: "",
    neetAir: "",
    neetScore: "",
    genDate: new Date().toISOString().substring(0, 10),
    category: "",
    reservations: []
  });

  const [isReservationOpen, setIsReservationOpen] = useState(false);
  const reservationRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (reservationRef.current && !reservationRef.current.contains(e.target)) {
        setIsReservationOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Generated checklist state
  const [isGenerated, setIsGenerated] = useState(false);
  const [checklistDocs, setChecklistDocs] = useState([]);
  const [checkedState, setCheckedState] = useState({});
  const [currentDateTime, setCurrentDateTime] = useState("");
  const pdfRef = useRef(null);

  // Auto-set today's date and current date-time for print
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      genDate: new Date().toISOString().substring(0, 10)
    }));

    const now = new Date();
    setCurrentDateTime(
      now.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric"
      }) + " | " + now.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit"
      })
    );
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGenerate = (e) => {
    e.preventDefault();
    if (!formData.category) {
      toast.error("Please select a Main Category first.");
      return;
    }

    // Category Documents (single select)
    const catDocs = CATEGORY_DOCS[formData.category] || [];

    // Merge and deduplicate Special Reservation Documents (multi-select)
    const resDocs = [];
    formData.reservations.forEach(res => {
      const docs = RESERVATION_DOCS[res]?.docs || [];
      docs.forEach(d => {
        if (!resDocs.map(x => x.toLowerCase()).includes(d.toLowerCase())) {
          resDocs.push(d);
        }
      });
    });

    // Deduplicate special reservation docs already in category docs
    const uniqueResDocs = resDocs.filter(
      d => !catDocs.map(x => x.toLowerCase()).includes(d.toLowerCase())
    );

    const merged = [
      ...catDocs.map(name => ({ name, type: "category" })),
      ...uniqueResDocs.map(name => ({ name, type: "reservation" }))
    ];

    setChecklistDocs(merged);

    // Reset checked state for all new indices
    const initialCheckState = {};
    merged.forEach((_, idx) => {
      initialCheckState[idx] = false;
    });
    setCheckedState(initialCheckState);
    setIsGenerated(true);
    toast.success("Document checklist generated!");
  };

  const handleCheckboxChange = (idx) => {
    setCheckedState(prev => {
      const newVal = !prev[idx];
      const docName = checklistDocs[idx]?.name || "Document";
      if (newVal) {
        toast.success(`Verified: ${docName}`);
      } else {
        toast.info(`Unverified: ${docName}`);
      }
      return { ...prev, [idx]: newVal };
    });
  };

  const handleVerifyAll = () => {
    const total = checklistDocs.length;
    const verifiedCount = Object.values(checkedState).filter(Boolean).length;
    const allChecked = verifiedCount === total;

    const newState = {};
    checklistDocs.forEach((_, idx) => {
      newState[idx] = !allChecked;
    });
    setCheckedState(newState);
    if (!allChecked) {
      toast.success("All documents marked as verified!");
    } else {
      toast.info("Cleared all verifications.");
    }
  };

  const handleReset = () => {
    setFormData({
      candName: "",
      regNo: "",
      neetAir: "",
      neetScore: "",
      genDate: new Date().toISOString().substring(0, 10),
      category: "",
      reservations: []
    });
    setIsGenerated(false);
    setChecklistDocs([]);
    setCheckedState({});
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    toast.success("Generating A4 verification PDF...");
    const element = pdfRef.current;
    if (!element) return;

    // Temporarily make visible for capture
    element.style.display = 'block';

    const opt = {
      margin: [0.4, 0.4, 0.4, 0.4],
      filename: `NEET_UG_Checklist_${formData.candName.replace(/\s+/g, '_') || 'Candidate'}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'] }
    };

    html2pdf().set(opt).from(element).save().then(() => {
      element.style.display = 'none';
    }).catch(err => {
      console.error(err);
      toast.error("Failed to generate PDF. Please try again.");
      element.style.display = 'none';
    });
  };

  // Stats calculation
  const totalCount = checklistDocs.length;
  const verifiedCount = Object.values(checkedState).filter(Boolean).length;
  const progressPercent = totalCount > 0 ? Math.round((verifiedCount / totalCount) * 100) : 0;
  const allChecked = totalCount > 0 && verifiedCount === totalCount;

  // Filter lists
  const catDocsList = checklistDocs.filter(d => d.type === "category");
  const resDocsList = checklistDocs.filter(d => d.type === "reservation");

  return (
    <>
      <Helmet>
        <title>Maharashtra Medical Admission Documents Checklist | Admissions Made Easy</title>
        <meta name="description" content="Generate your customized document checklist for Maharashtra NEET UG 2026 medical counselling based on your category and special reservation criteria." />
      </Helmet>

      {/* Light Mode Container styled with Brand Colors */}
      <div className="min-h-screen text-slate-800 font-sans pb-16" style={{ backgroundColor: '#f8fafc' }}>
        <div className="max-w-6xl mx-auto px-4 md:px-8">

          {/* Public Header bar with Back button */}
          <header className="flex justify-between items-center py-5 border-b border-slate-200 mb-8 no-print">
            <button
              onClick={() => navigate("/medical-admission")}
              className="flex items-center gap-2 bg-slate-100 border border-slate-200 hover:bg-slate-200 text-slate-700 hover:text-slate-900 px-5 py-2.5 rounded-full text-xs font-bold transition-all"
            >
              <ChevronLeft className="h-4 w-4" /> Back to Medical Admission
            </button>
            <div className="flex items-center gap-2.5 h-8">
              <img src="/logo.png" alt="AME Logo" className="h-full w-auto object-contain" />
              <span className="font-black text-sm tracking-tight text-[#0B1F45]">
                Admissions Made Easy
              </span>
            </div>
          </header>

          <div className="space-y-6">

            {/* TITLE & DESCRIPTION */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 no-print">
              <div>
                <h1 className="text-2xl font-black tracking-tight text-[#0B1F45]">
                  NEET UG 2026 Document Checklist
                </h1>
                <p className="text-sm mt-1 text-slate-600">
                  Generate &amp; verify Maharashtra medical counselling required documents based on your criteria.
                </p>
              </div>
              {isGenerated && (
                <button
                  onClick={() => setIsGenerated(false)}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition-all hover:scale-105 active:scale-95 text-slate-600 bg-slate-100 border border-slate-200 hover:text-slate-900 hover:bg-slate-200"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Go Back / Edit Info
                </button>
              )}
            </div>

            {/* INPUT FORM VIEW */}
            {!isGenerated ? (
              <div className="max-w-3xl p-8 space-y-6 overflow-visible no-print" style={cardStyle}>
                <div>
                  <h2 className="text-xl font-bold text-[#0B1F45] flex items-center gap-2">
                    <FileText className="h-5 w-5 text-[#C8973A]" />
                    Candidate Admission Details
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">
                    Please provide your NEET details and category criteria below to generate your custom required documents checklist.
                  </p>
                </div>

                <form onSubmit={handleGenerate} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                    {/* Full Name */}
                    <div className="space-y-1.5 md:col-span-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Candidate Name</label>
                      <div className="relative group">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 group-focus-within:text-[#0B1F45] transition-colors" />
                        <input
                          required
                          name="candName"
                          className="w-full h-12 pl-12 pr-4 rounded-xl bg-white border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#0B1F45] focus:ring-1 focus:ring-[#0B1F45] transition-all font-semibold"
                          placeholder="e.g. Aditi Deshmukh"
                          value={formData.candName}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Main Category */}
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Main Category *</label>
                      <select
                        required
                        name="category"
                        className="w-full h-12 px-4 rounded-xl bg-white border border-slate-200 text-sm text-slate-900 focus:outline-none focus:border-[#0B1F45] focus:ring-1 focus:ring-[#0B1F45] transition-all font-semibold cursor-pointer"
                        value={formData.category}
                        onChange={handleInputChange}
                      >
                        <option value="">-- Select Category --</option>
                        {Object.keys(CATEGORY_DOCS).map(cat => (
                          <option key={cat} value={cat}>{CAT_LABELS[cat] || cat}</option>
                        ))}
                      </select>
                    </div>

                    {/* Special Reservation (Multi-Select) */}
                    <div className="space-y-1.5 relative" ref={reservationRef}>
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Special Reservation</label>
                      <div
                        onClick={() => setIsReservationOpen(prev => !prev)}
                        className="w-full min-h-[48px] px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-sm text-slate-900 focus-within:border-[#0B1F45] focus-within:ring-1 focus-within:ring-[#0B1F45] transition-all flex flex-wrap items-center gap-1.5 cursor-pointer relative pr-10"
                      >
                        {formData.reservations.length === 0 ? (
                          <span className="text-slate-400">None / Not Applicable</span>
                        ) : (
                          formData.reservations.map(res => (
                            <span
                              key={res}
                              className="bg-amber-500/5 border border-amber-500/15 text-amber-700 px-2.5 py-1 rounded-lg text-xs font-bold flex items-center gap-1.5"
                              onClick={(e) => { e.stopPropagation(); setFormData(prev => ({ ...prev, reservations: prev.reservations.filter(x => x !== res) })); }}
                            >
                              {RESERVATION_DOCS[res]?.label || res}
                              <X className="h-3 w-3 hover:text-red-500 cursor-pointer" />
                            </span>
                          ))
                        )}
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-[10px]">▼</span>
                      </div>
                      {isReservationOpen && (
                        <div className="absolute top-[calc(100%+4px)] left-0 right-0 bg-white border border-slate-200 shadow-xl rounded-xl max-h-60 overflow-y-auto z-30 py-1.5">
                          {Object.entries(RESERVATION_DOCS).map(([key, meta]) => {
                            const selected = formData.reservations.includes(key);
                            return (
                              <div
                                key={key}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFormData(prev => {
                                    const next = prev.reservations.includes(key)
                                      ? prev.reservations.filter(x => x !== key)
                                      : [...prev.reservations, key];
                                    return { ...prev, reservations: next };
                                  });
                                }}
                                className="flex items-center gap-3 px-4 py-2 hover:bg-slate-50 cursor-pointer text-sm text-slate-700 font-semibold transition-colors"
                              >
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  onChange={() => { }}
                                  className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500 cursor-pointer"
                                />
                                {meta.label}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                  </div>

                  <button
                    type="submit"
                    className="w-full h-14 rounded-2xl font-black text-white flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-95 mt-4"
                    style={{ background: 'linear-gradient(135deg, #0B1F45, #162d5e)', boxShadow: '0 8px 25px rgba(11,31,69,0.2)' }}
                  >
                    <FileText className="h-5 w-5" />
                    Generate My Checklist
                  </button>
                </form>
              </div>
            ) : (
              /* INTERACTIVE CHECKLIST VIEW */
              <div className="space-y-6">

                {/* STATS / PROGRESS BAR */}
                <div className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-6 no-print" style={{ ...cardStyle, background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)' }}>
                  <div className="flex items-center gap-4 flex-1">
                    <div className="text-center bg-emerald-50 border border-emerald-200 px-4 py-2.5 rounded-2xl flex-shrink-0">
                      <span className="block text-2xl font-black text-emerald-700 leading-none">{verifiedCount}</span>
                      <span className="text-[10px] font-black uppercase text-slate-500 tracking-wider">Checked</span>
                    </div>
                    <div className="flex-grow">
                      <div className="flex justify-between items-center text-xs font-bold text-slate-600">
                        <span>Checklist Progress</span>
                        <span>{progressPercent}% Complete</span>
                      </div>
                      <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden mt-1.5 border border-slate-200/50">
                        <div
                          className="h-full rounded-full transition-all duration-300"
                          style={{ width: `${progressPercent}%`, background: 'linear-gradient(90deg, #10b981, #059669)' }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Actions Panel */}
                  <div className="flex flex-wrap items-center gap-2.5">
                    <button
                      onClick={handleVerifyAll}
                      className={`px-4 py-2 rounded-xl text-xs font-bold transition-all border flex items-center gap-1.5 ${allChecked
                        ? 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100/70'
                        : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100/70'
                        }`}
                    >
                      {allChecked ? <><X className="h-3.5 w-3.5" /> Clear All</> : <><Check className="h-3.5 w-3.5" /> Select All</>}
                    </button>

                    <button
                      onClick={handleDownloadPDF}
                      className="px-4 py-2 rounded-xl text-xs font-bold transition-all bg-[#0B1F45] text-white hover:bg-[#162d5e] flex items-center gap-1.5 shadow-md shadow-[#0B1F45]/10"
                    >
                      <Download className="h-3.5 w-3.5" />
                      Download PDF
                    </button>

                    <button
                      onClick={handlePrint}
                      className="px-4 py-2 rounded-xl text-xs font-bold transition-all bg-slate-100 border border-slate-200 text-slate-700 hover:bg-slate-200 flex items-center gap-1.5"
                    >
                      <Printer className="h-3.5 w-3.5" />
                      Print
                    </button>

                    <button
                      onClick={handleReset}
                      className="px-4 py-2 rounded-xl text-xs font-bold transition-all bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100/70 flex items-center gap-1.5"
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      Reset
                    </button>
                  </div>
                </div>

                {/* CANDIDATE INFO STRIP */}
                <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4 text-xs no-print" style={cardStyle}>
                  <div>
                    <span className="block text-[10px] uppercase font-bold text-slate-500 tracking-wider">Candidate Name</span>
                    <span className="font-extrabold text-[#0B1F45] text-sm block mt-0.5 truncate">{formData.candName || "—"}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase font-bold text-slate-500 tracking-wider">Category</span>
                    <span className="font-extrabold text-[#C8973A] text-sm block mt-0.5">{CAT_LABELS[formData.category] || formData.category || "—"}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase font-bold text-slate-500 tracking-wider">Special Reservations</span>
                    <span className="font-extrabold text-[#0B1F45] text-sm block mt-0.5">{formData.reservations.length > 0 ? formData.reservations.map(r => RESERVATION_DOCS[r]?.label || r).join(', ') : "None"}</span>
                  </div>
                </div>

                {/* CHECKLIST TABLES SCREEN VIEW */}
                <div className="space-y-6 no-print">
                  {/* Category Documents Table */}
                  {catDocsList.length > 0 && (
                    <div className="p-6 space-y-4" style={cardStyle}>
                      <h3 className="font-bold text-base text-[#0B1F45] border-b border-slate-100 pb-2.5 flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                        Category Required Documents
                        <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 rounded-lg uppercase tracking-wider font-extrabold ml-1.5 inline-block">
                          {CAT_LABELS[formData.category] || formData.category}
                        </span>
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs border-collapse">
                          <thead>
                            <tr className="border-b border-slate-200 text-slate-500">
                              <th className="py-3 px-4 font-bold uppercase tracking-wider w-16">Sr.</th>
                              <th className="py-3 px-4 font-bold uppercase tracking-wider">Document Required</th>
                              <th className="py-3 px-4 font-bold uppercase tracking-wider text-center w-28">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {checklistDocs.map((doc, idx) => {
                              if (doc.type !== "category") return null;
                              const isVerified = !!checkedState[idx];
                              return (
                                <tr
                                  key={idx}
                                  onClick={() => handleCheckboxChange(idx)}
                                  className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${isVerified ? 'bg-emerald-50/40' : ''}`}
                                >
                                  <td className="py-3 px-4 text-slate-500 font-bold">{idx + 1}</td>
                                  <td className={`py-3 px-4 font-semibold ${isVerified ? 'text-emerald-700 line-through opacity-75' : 'text-slate-800'}`}>{doc.name}</td>
                                  <td className="py-3 px-4 text-center">
                                    <input
                                      type="checkbox"
                                      className="w-4 h-4 rounded border-slate-300 bg-white text-emerald-600 focus:ring-emerald-500 focus:ring-offset-white cursor-pointer"
                                      checked={isVerified}
                                      onChange={() => { }} // Row click handles state change
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Special Reservation Documents Table */}
                  {resDocsList.length > 0 && (
                    <div className="p-6 space-y-4" style={cardStyle}>
                      <h3 className="font-bold text-base text-[#0B1F45] border-b border-slate-100 pb-2.5 flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 text-blue-600" />
                        Special Reservation Documents
                        <span className="text-[10px] bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-1 rounded-lg uppercase tracking-wider font-extrabold ml-1.5 inline-block">
                          {formData.reservations.map(r => RESERVATION_DOCS[r]?.label || r).join(', ')}
                        </span>
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs border-collapse">
                          <thead>
                            <tr className="border-b border-slate-200 text-slate-500">
                              <th className="py-3 px-4 font-bold uppercase tracking-wider w-16">Sr.</th>
                              <th className="py-3 px-4 font-bold uppercase tracking-wider">Document Required</th>
                              <th className="py-3 px-4 font-bold uppercase tracking-wider text-center w-28">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {checklistDocs.map((doc, idx) => {
                              if (doc.type !== "reservation") return null;
                              const isVerified = !!checkedState[idx];
                              return (
                                <tr
                                  key={idx}
                                  onClick={() => handleCheckboxChange(idx)}
                                  className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${isVerified ? 'bg-emerald-50/40' : ''}`}
                                >
                                  <td className="py-3 px-4 text-slate-500 font-bold">{idx + 1}</td>
                                  <td className={`py-3 px-4 font-semibold ${isVerified ? 'text-emerald-700 line-through opacity-75' : 'text-slate-800'}`}>{doc.name}</td>
                                  <td className="py-3 px-4 text-center">
                                    <input
                                      type="checkbox"
                                      className="w-4 h-4 rounded border-slate-300 bg-white text-emerald-600 focus:ring-emerald-500 focus:ring-offset-white cursor-pointer"
                                      checked={isVerified}
                                      onChange={() => { }} // Row click handles state change
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>

                {/* GUIDANCE ALERT BAR */}
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-3 text-xs text-amber-800 leading-relaxed no-print">
                  <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-amber-700 font-bold block mb-0.5">Crucial Verification Guidance</strong>
                    Ensure that all certificates match the spelling of the name printed on the candidate's NEET UG 2026 Admit Card exactly. If there is a spelling mismatch, carry a notarized <a href="https://drive.google.com/file/d/1qTDPvWdoPj68LPqjiuAlLViy6dvJGuTm/view?usp=drive_link" target="_blank" rel="noopener noreferrer" style={{ color: "#b45309", textDecoration: "underline", fontWeight: "700" }}>Name Change Affidavit</a> along with these files.
                  </div>
                </div>

              </div>
            )}

          </div>

          {/* HIDDEN TEMPLATE FOR PRINTING & PDF EXPORT */}
          <div style={{ display: 'none' }}>
            <div
              ref={pdfRef}
              style={{
                background: 'white',
                color: 'black',
                fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                padding: '24px',
                maxWidth: '800px',
                margin: '0 auto',
                position: 'relative'
              }}
            >
              {/* Watermark Logo in Background */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                opacity: 0.04,
                width: '400px',
                height: '400px',
                zIndex: 0,
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <img src="/logo.png" alt="watermark" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </div>

              <div style={{ position: 'relative', zIndex: 1 }}>
                {/* Header / Branding */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2.5px solid #0B1F45', paddingBottom: '12px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <img src="/logo.png" alt="logo" style={{ width: '48px', height: '48px', objectFit: 'contain' }} />
                    <div>
                      <h1 style={{ fontSize: '20px', fontWeight: '800', color: '#0B1F45', margin: 0, letterSpacing: '-0.3px' }}>
                        Admissions Made Easy
                      </h1>
                      <p style={{ fontSize: '11px', color: '#C8973A', margin: '2px 0 0 0', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        NEET-UG 2026 Document Verification Checklist
                      </p>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: '9px', color: '#64748b', fontWeight: 'bold' }}>
                    Verified on: {new Date(formData.genDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </div>
                </div>

                {/* Profile Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px 20px', background: '#FAF7F2', border: '1px solid #d8d5ce', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', fontSize: '11px' }}>
                  <div>
                    <strong style={{ color: '#8a8a9a', fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Candidate Name</strong>
                    <div style={{ fontWeight: 'bold', color: '#0B1F45', marginTop: '2px' }}>{formData.candName || "—"}</div>
                  </div>
                  <div>
                    <strong style={{ color: '#8a8a9a', fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Category</strong>
                    <div style={{ fontWeight: 'bold', color: '#C8973A', marginTop: '2px' }}>{CAT_LABELS[formData.category] || formData.category || "—"}</div>
                  </div>
                  <div>
                    <strong style={{ color: '#8a8a9a', fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Special Reservation</strong>
                    <div style={{ fontWeight: 'bold', color: '#162d5e', marginTop: '2px' }}>{formData.reservations.length > 0 ? formData.reservations.map(r => RESERVATION_DOCS[r]?.label || r).join(', ') : "None"}</div>
                  </div>
                </div>

                {/* Checklist Tables */}

                {/* Category required docs table */}
                {catDocsList.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ fontSize: '12px', fontWeight: '800', color: '#0B1F45', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Category Documents ({CAT_LABELS[formData.category] || formData.category})
                    </h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10.5px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#0B1F45', color: 'white' }}>
                          <th style={{ padding: '6px 8px', fontWeight: 'bold', textAlign: 'center', width: '35px' }}>Sr.</th>
                          <th style={{ padding: '6px 8px', fontWeight: 'bold', textAlign: 'left' }}>Required Document Name</th>
                          <th style={{ padding: '6px 8px', fontWeight: 'bold', textAlign: 'center', width: '80px' }}>Verified ✓</th>
                        </tr>
                      </thead>
                      <tbody>
                        {checklistDocs.map((doc, idx) => {
                          if (doc.type !== "category") return null;
                          const isVerified = !!checkedState[idx];
                          return (
                            <tr key={idx} style={{ borderBottom: '1px solid #d8d5ce', backgroundColor: isVerified ? '#f0faf4' : 'transparent' }}>
                              <td style={{ padding: '6px 8px', textAlign: 'center', color: '#8a8a9a', fontWeight: 'bold' }}>{idx + 1}</td>
                              <td style={{ padding: '6px 8px', color: isVerified ? '#1a6b3c' : '#1a1a2e', fontWeight: isVerified ? 'bold' : 'normal' }}>{doc.name}</td>
                              <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: '12px', color: '#1a6b3c', fontWeight: 'bold' }}>
                                {isVerified ? "✓" : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Special reservation documents table */}
                {resDocsList.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ fontSize: '12px', fontWeight: '800', color: '#0B1F45', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Special Reservation Documents ({formData.reservations.map(r => RESERVATION_DOCS[r]?.label || r).join(', ')})
                    </h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10.5px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#0B1F45', color: 'white' }}>
                          <th style={{ padding: '6px 8px', fontWeight: 'bold', textAlign: 'center', width: '35px' }}>Sr.</th>
                          <th style={{ padding: '6px 8px', fontWeight: 'bold', textAlign: 'left' }}>Required Document Name</th>
                          <th style={{ padding: '6px 8px', fontWeight: 'bold', textAlign: 'center', width: '80px' }}>Verified ✓</th>
                        </tr>
                      </thead>
                      <tbody>
                        {checklistDocs.map((doc, idx) => {
                          if (doc.type !== "reservation") return null;
                          const isVerified = !!checkedState[idx];
                          return (
                            <tr key={idx} style={{ borderBottom: '1px solid #d8d5ce', backgroundColor: isVerified ? '#f0faf4' : 'transparent' }}>
                              <td style={{ padding: '6px 8px', textAlign: 'center', color: '#8a8a9a', fontWeight: 'bold' }}>{idx + 1}</td>
                              <td style={{ padding: '6px 8px', color: isVerified ? '#1a6b3c' : '#1a1a2e', fontWeight: isVerified ? 'bold' : 'normal' }}>{doc.name}</td>
                              <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: '12px', color: '#1a6b3c', fontWeight: 'bold' }}>
                                {isVerified ? "✓" : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Bottom Signatures */}
                <div style={{ marginTop: '45px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '50px', fontSize: '11px', pageBreakInside: 'avoid' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ height: '35px' }}></div>
                    <div style={{ borderTop: '1.5px solid #1a1a2e', paddingTop: '6px', fontWeight: 'bold', color: '#0B1F45' }}>
                      Candidate Signature
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ height: '35px', fontSize: '12px', fontStyle: 'italic', color: '#8a8a9a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      Verified Digitally
                    </div>
                    <div style={{ borderTop: '1.5px solid #1a1a2e', paddingTop: '6px', fontWeight: 'bold', color: '#0B1F45' }}>
                      Counselor Signature
                    </div>
                  </div>
                </div>

                {/* Disclaimer */}
                <div style={{ marginTop: '24px', borderTop: '1px dashed #d8d5ce', paddingTop: '10px', fontSize: '9px', color: '#8a8a9a', fontStyle: 'italic', lineHeight: '1.4', pageBreakInside: 'avoid' }}>
                  Disclaimer: This verification checklist is dynamically generated based on details submitted by the counselor. Please keep original files and 3 sets of attested photocopies ready for state reporting.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
